import { FC, useEffect, useRef } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Form } from 'antd';
import { v4 as uuidv4 } from 'uuid';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { ButtonIwText } from '@/_pages/interwill/components/ButtonIwText';
import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { ExcludedPerson } from '@/entities/interwills';
import { Input } from '@/shared/components';
import { useIsDesktop } from '@/shared/hooks/use-is-desktop';
import { Icon } from '@/shared/uikit/Icon';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { ExcludedPersonsModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';
import {
  InterwillFormContainer,
  InterwillStyledForm,
  InterwillSubmitButtonWrapper,
} from '../CreateEntityModal/styled';

import { BtnClose, Container, FieldsGroup, Title } from './styled';

export type PersonItem = ExcludedPerson & { uuid: string };

export const ExcludedPersonsModal: FC<ExcludedPersonsModalProps> = ({ list, onSubmit }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'excludedPersons' });
  const refDiv = useRef<HTMLDivElement | null>(null);

  const modals = useModalsStore();
  const isDesktop = useIsDesktop();

  const defaultValuesItems = list.map((item) => ({ ...item, uuid: uuidv4() }));

  const {
    control,
    formState,
    getFieldState,
    register,
    setValue,
    handleSubmit: formHandleSubmit,
  } = useForm<{ items: PersonItem[] }>({
    defaultValues: { items: defaultValuesItems },
  });

  const fieldsArray = useFieldArray({
    control,
    name: 'items',
  });

  const handleAdd = () => {
    const newItem: PersonItem = {
      uuid: uuidv4(),
      firstName: '',
      secondName: '',
      lastName: '',
    };

    fieldsArray.append(newItem);

    setTimeout(() => refDiv.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleRemove = (index: number) => {
    fieldsArray.remove(index);
  };

  const handleSubmit = (data: { items: PersonItem[] }) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const newList = data.items.map(({ uuid, ...item }) => item);
    modals.closeModal();
    onSubmit(newList);
  };

  useEffect(() => {
    if (list.length === 0) {
      handleAdd();
    }
  }, []);

  return (
    <ModalContainerV1 title={t('title')}>
      <InterwillStyledForm onFinish={formHandleSubmit(handleSubmit)}>
        <InterwillFormContainer>
          {fieldsArray.fields.map((field, index) => {
            type FieldName = Parameters<typeof getFieldState>[0];

            const fldValidateStatus = (fieldName: FieldName): '' | 'error' => {
              const hasError = getFieldState(fieldName, formState).error !== undefined;
              return hasError ? 'error' : '';
            };
            const fldRegister = (fldName: FieldName, required?: boolean) => {
              return register(fldName, {
                required,
                onChange: (ev) => setValue(fldName, ev.target.value),
              });
            };

            const fieldFirstName = fldRegister(`items.${index}.firstName`, true);
            const fieldLastName = fldRegister(`items.${index}.lastName`, true);
            const fieldSecondName = fldRegister(`items.${index}.secondName`);

            return (
              <Container key={field.id}>
                <Title>
                  <span>Disinherited person {index + 1}</span>
                  <BtnClose onClick={() => handleRemove(index)} type="button">
                    <Icon name="x" size="s16" />
                  </BtnClose>
                </Title>
                <FieldsGroup>
                  <FieldContainerIw label={t('fields.firstName.label')} required>
                    <Form.Item validateStatus={fldValidateStatus(`items.${index}.firstName`)}>
                      <Input
                        placeholder={t('fields.firstName.placeholder')}
                        {...fieldFirstName}
                        defaultValue={field.firstName}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                  <FieldContainerIw label={t('fields.secondName.label')}>
                    <Form.Item>
                      <Input
                        placeholder={t('fields.secondName.placeholder')}
                        {...fieldSecondName}
                        defaultValue={field.secondName}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                </FieldsGroup>
                <FieldContainerIw label={t('fields.lastName.label')} required>
                  <Form.Item validateStatus={fldValidateStatus(`items.${index}.lastName`)}>
                    <Input
                      placeholder={t('fields.lastName.placeholder')}
                      {...fieldLastName}
                      defaultValue={field.lastName}
                    />
                  </Form.Item>
                </FieldContainerIw>
              </Container>
            );
          })}
          <div ref={refDiv}>
            <ButtonIwText type="text" onClick={handleAdd}>
              <Icon name="plus" size="s20" />
              {t('btnNew')}
            </ButtonIwText>
          </div>
        </InterwillFormContainer>

        <InterwillSubmitButtonWrapper>
          <ButtonIw
            htmlType="submit"
            block={!isDesktop}
            disabled={formState.isSubmitting}
            loading={formState.isSubmitting}
          >
            {t('btnSubmit')}
          </ButtonIw>
        </InterwillSubmitButtonWrapper>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};
